<template>
  <div class="voice-assistant-overlay" @click.self="handleClose">
    <div class="voice-assistant-modal">
      <!-- Header -->
      <div class="modal-header">
        <h2>{{ headerText }}</h2>
        <button class="close-btn" @click="handleClose" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Connection Status Indicator -->
      <div class="connection-status" :class="connectionStatusClass">
        <span class="status-dot"></span>
        <span class="status-text">{{ connectionStatusText }}</span>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error-banner">
        {{ error }}
      </div>

      <!-- Voice Animation Section -->
      <div class="voice-animation-section">
        <div
          class="voice-circle"
          :class="{
            'is-listening': isRecording,
            'is-processing': isConnecting
          }"
        >
          <div class="pulse-ring pulse-ring-1"></div>
          <div class="pulse-ring pulse-ring-2"></div>
          <div class="pulse-ring pulse-ring-3"></div>

          <!-- Connecting Pulses -->
          <div v-if="isConnecting" class="connecting-pulses">
            <div class="ring-orbit ring-1"></div>
            <div class="ring-orbit ring-2"></div>
            <div class="ring-orbit ring-3"></div>
          </div>

          <!-- Audio Level Ring -->
          <div
            v-if="isRecording"
            class="audio-level-ring"
            :style="{ transform: `scale(${1 + audioLevel / 100})`, opacity: audioLevel / 150 }"
          ></div>

          <div class="mic-center" @click="handleMicClick">
            <!-- Mic Icon (idle state) -->
            <svg v-if="!isRecording && !isConnecting" width="32" height="32" viewBox="0 0 24 24" fill="white">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>

            <!-- Sound Waves (recording state) -->
            <div v-else-if="isRecording" class="sound-waves">
              <span class="wave" :style="{ height: `${8 + audioLevel * 0.2}px` }"></span>
              <span class="wave" :style="{ height: `${8 + audioLevel * 0.25}px` }"></span>
              <span class="wave" :style="{ height: `${8 + audioLevel * 0.3}px` }"></span>
              <span class="wave" :style="{ height: `${8 + audioLevel * 0.25}px` }"></span>
              <span class="wave" :style="{ height: `${8 + audioLevel * 0.2}px` }"></span>
            </div>

            <!-- Loading Spinner (connecting state) -->
            <div v-else class="connecting-content">
              <div class="loading-spinner"></div>
              <span class="wait-text">Wait</span>
            </div>
          </div>
        </div>

        <!-- Live Transcript Display (while recording) -->
        <div v-if="isRecording" class="transcript-section live-transcript">
          <div ref="transcriptDisplay" class="transcript-display">
            <p class="final-text">{{ finalTranscript }}<span v-if="interimTranscript" class="interim-text">{{ interimTranscript }}</span></p>
            <span v-if="!finalTranscript && !interimTranscript" class="placeholder-text">Start speaking...</span>
          </div>

          <!-- Transcript Hint -->
          <p class="transcript-hint">
            <span class="live-indicator"></span>
            Live transcription active
          </p>
        </div>

        <!-- Editable Transcript (after done recording) -->
        <div v-else-if="hasTranscript && !isRecording" class="transcript-section">
          <textarea
            v-model="editableTranscript"
            class="transcript-textarea"
            rows="4"
            placeholder="Transcript will appear here..."
          ></textarea>
        </div>

        <!-- Status Text (only when no transcript and not recording) -->
        <p v-if="!hasTranscript && !isRecording" class="status-text">
          <span v-if="isConnecting">Connecting...</span>
          <span v-else>Tap to start listening</span>
        </p>
      </div>

      <!-- Bottom Actions -->
      <div class="bottom-actions">
        <button class="action-btn keyboard-btn" @click="handleKeyboardInput" aria-label="Type instead">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="4" width="20" height="16" rx="2" ry="2"></rect>
            <line x1="6" y1="8" x2="6" y2="8"></line>
            <line x1="10" y1="8" x2="10" y2="8"></line>
            <line x1="14" y1="8" x2="14" y2="8"></line>
            <line x1="18" y1="8" x2="18" y2="8"></line>
            <line x1="6" y1="12" x2="6" y2="12"></line>
            <line x1="10" y1="12" x2="10" y2="12"></line>
            <line x1="14" y1="12" x2="14" y2="12"></line>
            <line x1="18" y1="12" x2="18" y2="12"></line>
            <line x1="8" y1="16" x2="16" y2="16"></line>
          </svg>
        </button>

        <!-- Show Done button while listening -->
        <button
          v-if="isRecording"
          class="action-btn done-btn"
          @click="handleDone"
          aria-label="Done"
        >
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#4caf50" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="9 12 12 15 16 10"/>
          </svg>
        </button>

        <!-- Show confirm button when transcript is ready -->
        <button
          v-else-if="hasTranscript && !isRecording"
          class="action-btn confirm-btn"
          @click="handleConfirm"
          aria-label="Confirm"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>

        <!-- Show cancel button otherwise -->
        <button
          v-else
          class="action-btn cancel-btn"
          @click="handleCancel"
          :disabled="isConnecting"
          aria-label="Cancel"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <line x1="18" y1="6" x2="6" y2="18" stroke="white" stroke-width="3"></line>
            <line x1="6" y1="6" x2="18" y2="18" stroke="white" stroke-width="3"></line>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'VoiceAssistant',

  data() {
    return {
      justStopped: false
    }
  },

  computed: {
    ...mapState({
      isRecording: state => state.transcription.isRecording,
      isConnecting: state => state.transcription.isConnecting,
      isConnected: state => state.transcription.isConnected,
      finalTranscript: state => state.transcription.finalTranscript,
      interimTranscript: state => state.transcription.interimTranscript,
      error: state => state.transcription.error,
      audioLevel: state => state.transcription.audioLevel
    }),

    hasTranscript() {
      return this.finalTranscript && this.finalTranscript.trim().length > 0
    },

    headerText() {
      if (this.isRecording) return "I'm listening..."
      if (this.isConnecting) return "Connecting..."
      if (this.hasTranscript) return 'Review your message'
      return 'How may I assist you?'
    },

    connectionStatusClass() {
      return {
        'status-connected': this.isConnected,
        'status-connecting': this.isConnecting,
        'status-disconnected': !this.isConnected && !this.isConnecting,
        'status-recording': this.isRecording
      }
    },

    connectionStatusText() {
      if (this.isRecording && this.isConnected) return 'Microphone active • Connected'
      if (this.isRecording && !this.isConnected) return 'Microphone active • Offline mode'
      if (this.isConnecting) return 'Please wait, establishing connection...'
      if (this.isConnected) return 'Ready to listen'
      return 'Tap mic to start'
    },

    editableTranscript: {
      get() {
        return this.finalTranscript
      },
      set(value) {
        this.$store.commit('SET_TRANSCRIPTION_STATE', { finalTranscript: value })
      }
    }
  },

  watch: {
    finalTranscript() {
      this.$nextTick(() => this.scrollToBottom())
    },
    interimTranscript() {
      this.$nextTick(() => this.scrollToBottom())
    },
    isRecording(recording, wasRecording) {
      if (wasRecording && !recording) {
        this.justStopped = true
        setTimeout(() => { this.justStopped = false }, 4000)
      }
    }
  },

  mounted() {
    this.startListening()
  },

  beforeDestroy() {
    this.stopTranscription()
    this.disconnectTranscription()
  },

  methods: {
    ...mapActions([
      'startTranscription',
      'stopTranscription',
      'clearTranscripts',
      'disconnectTranscription'
    ]),

    scrollToBottom() {
      if (this.$refs.transcriptDisplay) {
        this.$refs.transcriptDisplay.scrollTop = this.$refs.transcriptDisplay.scrollHeight
      }
    },

    async startListening() {
      if (this.isRecording || this.isConnecting) return
      this.clearTranscripts()
      const success = await this.startTranscription()
      if (!success) {
        console.error('[VoiceAssistant] Failed to start recording')
      }
    },

    handleMicClick() {
      if (!this.isRecording && !this.isConnecting) {
        this.startListening()
      }
    },

    handleDone() {
      this.stopTranscription()
      console.log('=== DONE LISTENING - FINAL TRANSCRIPT ===')
      console.log(this.finalTranscript)
      console.log('=========================================')
    },

    handleConfirm() {
      const transcript = this.finalTranscript.trim()
      this.$store.dispatch('setPendingTranscript', transcript)
      this.$router.push({ path: '/voice/result' })
      this.$emit('close')
    },

    handleCancel() {
      this.stopTranscription()
      this.disconnectTranscription()
      this.$emit('close')
    },

    handleClose() {
      this.stopTranscription()
      this.disconnectTranscription()
      this.$emit('close')
    },

    handleKeyboardInput() {
      this.$emit('keyboard-input')
    }
  }
}
</script>

<style scoped>
.voice-assistant-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.voice-assistant-modal {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

/* Connection Status */
.connection-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ccc;
  transition: all 0.3s ease;
}

.status-connected .status-dot {
  background: #4caf50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.5);
}

.status-connecting .status-dot {
  background: #ff9800;
  animation: pulse-dot 1s ease-in-out infinite;
}

.status-recording .status-dot {
  background: #f44336;
  animation: pulse-dot 0.8s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
}

.connection-status .status-text {
  font-weight: 500;
}

/* Error Banner */
.error-banner {
  background: #fee;
  border: 1px solid #fcc;
  color: #c00;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

/* Voice Animation Section */
.voice-animation-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

.voice-circle {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, rgba(102, 126, 234, 0.05) 50%, transparent 70%);
  opacity: 0;
}

.voice-circle.is-listening .pulse-ring {
  animation: pulse 2s ease-out infinite;
}

.voice-circle.is-listening .pulse-ring-1 {
  animation-delay: 0s;
}

.voice-circle.is-listening .pulse-ring-2 {
  animation-delay: 0.5s;
}

.voice-circle.is-listening .pulse-ring-3 {
  animation-delay: 1s;
}

@keyframes pulse {
  0% {
    transform: scale(0.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

/* Audio Level Ring */
.audio-level-ring {
  position: absolute;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  border: 3px solid rgba(102, 126, 234, 0.4);
  transition: transform 0.05s ease-out, opacity 0.05s ease-out;
  pointer-events: none;
}

.mic-center {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  z-index: 1;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.mic-center:hover {
  transform: scale(1.05);
}

.mic-center:active {
  transform: scale(0.98);
}

/* Sound waves animation */
.sound-waves {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
}

.wave {
  width: 4px;
  min-height: 8px;
  max-height: 28px;
  background: white;
  border-radius: 2px;
  transition: height 0.05s ease-out;
  animation: wave 0.8s ease-in-out infinite;
}

.wave:nth-child(1) { animation-delay: 0s; }
.wave:nth-child(2) { animation-delay: 0.1s; }
.wave:nth-child(3) { animation-delay: 0.2s; }
.wave:nth-child(4) { animation-delay: 0.3s; }
.wave:nth-child(5) { animation-delay: 0.4s; }

@keyframes wave {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(1.5);
  }
}

/* Loading spinner */
.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Connecting Pulses */
.connecting-pulses {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.ring-orbit {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;
}

.ring-1 {
  width: 120px;
  height: 120px;
  border-top-color: #667eea;
  animation: spin 1.5s linear infinite;
}

.ring-2 {
  width: 140px;
  height: 140px;
  border-right-color: #764ba2;
  animation: spin 2s linear infinite reverse;
}

.ring-3 {
  width: 160px;
  height: 160px;
  border-bottom-color: #ff9800;
  animation: spin 3s linear infinite;
}

.connecting-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.wait-text {
  color: white;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.voice-animation-section > .status-text {
  margin-top: 1.5rem;
  font-size: 1rem;
  color: #666;
  font-weight: 500;
}

/* Transcript Section */
.transcript-section {
  width: 100%;
  max-width: 400px;
  margin-top: 1.5rem;
  text-align: left;
}

.live-transcript {
  padding: 1.5rem;
  background: #f8f9ff;
  border-radius: 12px;
  border: 2px solid #667eea;
  animation: pulse-border 2s ease-in-out infinite;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: #667eea;
  }
  50% {
    border-color: #764ba2;
  }
}

.transcript-display {
  min-height: 80px;
  max-height: 150px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.final-text {
  margin: 0;
  color: #1a1a2e;
  font-size: 1rem;
  line-height: 1.6;
  font-weight: 500;
}

.interim-text {
  color: #888;
  font-style: italic;
  opacity: 0.8;
  animation: fade-pulse 1s ease-in-out infinite;
}

@keyframes fade-pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.placeholder-text {
  color: #aaa;
  font-style: italic;
}

.transcript-hint {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #999;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Live indicator dot */
.live-indicator {
  width: 8px;
  height: 8px;
  background: #f44336;
  border-radius: 50%;
  animation: pulse-dot 1s ease-in-out infinite;
}

.transcript-textarea {
  width: 100%;
  min-height: 100px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #f8f9ff;
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  outline: none;
  line-height: 1.6;
  color: #333;
  box-sizing: border-box;
}

.transcript-textarea:focus {
  border-color: #667eea;
}

/* Bottom Actions */
.bottom-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 0 2rem;
}

.action-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.keyboard-btn {
  background: white;
  border: 2px solid #e0e0e0;
  color: #666;
}

.keyboard-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.cancel-btn {
  background: #f44336;
  color: white;
}

.cancel-btn:hover:not(:disabled) {
  background: #d32f2f;
}

.confirm-btn {
  background: #4caf50;
  color: white;
}

.confirm-btn:hover {
  background: #43a047;
}

.done-btn {
  color: white;
}

.done-btn:hover {
  background: rgba(76, 175, 80, 0.1);
}

/* Processing state */
.voice-circle.is-processing .mic-center {
  background: linear-gradient(135deg, #424242 0%, #212121 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  animation: pulse-dim 2s ease-in-out infinite;
}

@keyframes pulse-dim {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(0.95);
  }
}
</style>
